import { Link } from "@eworkit/iris";
import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { IConsultantProfile, TCompletenessItemKey } from "@/data/consultant-profiles";
import { useToggle } from "@/hooks/Toggle";
import { useToasts } from "@/views/components/Toast/useToasts";
import { CompletenessModal } from "@/views/ConsultantProfiles/Completeness/components/CompletenessModal";
import { isCompletenessItemCompleted } from "@/views/ConsultantProfiles/Completeness/service";

const hints: TCompletenessItemKey[] = ["ROLE", "PHONE", "LOCATION", "SKILL", "LANGUAGE"];

interface IDashboardProfileHintsProps {
  profile: IConsultantProfile;
  onProfileUpdate: (profile: IConsultantProfile) => void;
}

export const DashboardProfileHints: FC<IDashboardProfileHintsProps> = ({ profile, onProfileUpdate }) => {
  const { t } = useTranslation("session");
  const { addToast } = useToasts();

  const [modalOpen, toggleModalOpen] = useToggle(false);
  const [selectedSection, setSelectedSection] = useState<TCompletenessItemKey | null>(null);

  const hint = hints.find(h => !isCompletenessItemCompleted(h, profile.completeness.completed));

  const openSection = useCallback(
    (section: TCompletenessItemKey) => () => {
      if (!isCompletenessItemCompleted(section, profile.completeness.completed)) {
        setSelectedSection(section);
        toggleModalOpen();
      }
    },
    [profile.completeness.completed, setSelectedSection, toggleModalOpen],
  );

  const handleUpdate = useCallback(
    (source: IConsultantProfile) => {
      addToast({ text: t("CONSULTANT_PROFILES.DETAIL.SUCCESS_MESSAGE"), type: "success" });

      onProfileUpdate(source);
    },
    [addToast, onProfileUpdate, t],
  );

  if (!hint) {
    return null;
  }

  return (
    <>
      <Link onClick={openSection(hint)}>{t(`DASHBOARD.PROFILE_SECTION.ADD_${hint}`)}</Link>
      <CompletenessModal
        onClose={toggleModalOpen}
        overflowX="auto"
        overflowY="auto"
        open={modalOpen}
        profile={profile}
        section={selectedSection}
        update={handleUpdate}
        size="sm"
      />
    </>
  );
};
